/// <amd-module name="Feed/Legacy/ControllerViews/FeedControllerView" />
import * as React from "react";
import { connect } from "react-redux";

import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Hub } from "VSSUI/Components/Hub/Hub";
import { HubHeader } from "VSSUI/Components/HubHeader/HubHeader";
import { MessageBarSeverity } from "VSSUI/MessageBar";
import { IPivotBarAction, PivotBarItem } from "VSSUI/PivotBar";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";

import { HubViewDefaultPivots } from "Feed/Common/Constants/Constants";
import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { MessageState } from "Feed/Common/Types/IFeedMessage";
import { DeletedFeedPanel } from "Feed/Core/Components/DeletedFeedPanel";
import { FeedPicker } from "Feed/Core/Components/FeedPicker";
import { IFeedHubViewAwareState } from "Feed/Core/Redux/FeedHub";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { FeedActionCreator } from "Feed/Legacy/Actions/FeedActionCreator";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { SettingsActions } from "Feed/Legacy/Actions/SettingsActions";
import { OpenConnectToFeedDialog } from "Feed/Legacy/Components/ConnectToFeedButton";
import { CreateBadgePanel } from "Feed/Legacy/Components/CreateBadgePanel";
import { EmptyFeedPanel } from "Feed/Legacy/Components/EmptyFeedPanel";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { MultiPromotePanel } from "Feed/Legacy/Components/MultiPromotePanel";
import { PackageFilterBar } from "Feed/Legacy/Components/PackageFilterBar";
import { IPackageGridProps, PackageGrid } from "Feed/Legacy/Components/PackageGrid";
import { WrappedCreateFeedPanel } from "Feed/Legacy/Components/WrappedCreateFeedPanel";
import { WrappedIvyPromotePanel } from "Feed/Legacy/Components/WrappedIvyPromotePanel";
import { WrappedZeroFeedsPage } from "Feed/Legacy/Components/WrappedZeroFeedsPage";
import { ILegacyControllerViewProps } from "Feed/Legacy/ControllerViews/ILegacyControllerViewProps";
import * as CommandGetters from "Feed/Legacy/Helpers/CommandGetters";
import { FeedPickerHelper } from "Feed/Legacy/Helpers/FeedPickerHelper";
import { PackageFilterBarHelper } from "Feed/Legacy/Helpers/PackageFilterBarHelper";
import { PackageGridMessages } from "Feed/Legacy/Helpers/PackageGridMessages";
import { hasAccessToBaseFeed } from "Feed/Legacy/Helpers/PermissionHelper";
import * as PivotGetters from "Feed/Legacy/Helpers/PivotBarActionGetter";
import { getCurrentSelectedProjectId, isPublicFeed } from "Feed/Legacy/Helpers/ProjectHelper";
import { FeedStore } from "Feed/Legacy/Stores/FeedStore";
import { FeedState } from "Feed/Legacy/Types/FeedState";
import { HubAction } from "Feed/Legacy/Types/IHubState";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

const Actions = {
    showConnectToFeed: FeedHubActions.connectToFeed,
    showCreateFeed: FeedHubActions.showCreateFeed,
    showIvyPromote: FeedHubActions.showIvyPromote,
    showGlobalSettings: FeedHubActions.showGlobalSettings
};

export interface IFeedControllerViewProps {
    fluxStore: FeedStore;
}

export interface IBannerMessage {
    messageBarType: MessageBarSeverity;
    message: string;
    bannerLocalStorageStatus: string;
}

export class FeedControllerView extends VssComponent<
    IFeedControllerViewProps & typeof Actions,
    FeedState
    > {
    constructor(
        props: IFeedControllerViewProps & typeof Actions,
        context?: IVssComponentContext
    ) {
        super(props, context);
        this.state = this.getState();
        this.actionCreator = new FeedActionCreator(context.pageContext);
        this.isNewHeaderEnabled = isFeatureFlagEnabled(context.pageContext, "Packaging.Feed.FeedRecycleBin.EnableUI");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.fluxStore.addChangedListener(this.triggerRender);
        this.actionCreator.initializeListeners();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.actionCreator.detachListeners();
        this.props.fluxStore.removeChangedListener(this.triggerRender);
    }

    public render(): JSX.Element {
        this.state = this.getState();
        return (
            <div className="packages-controllerview feed-controller-view relative">
                {this.state.displayWelcomeMessage ? (
                    <WrappedZeroFeedsPage />
                ) : (
                        <div className="feed-content">
                            {/*hidden h1 for accessibility*/
                                this.state.selectedFeed ? (
                                    <h1 className="visibly-hidden">{this.state.selectedFeed.name}</h1>
                                ) : null}
                            <Hub
                                // absolute-fill is required for scrollbar to work

                                // and this is above the element that has data-is-scrollable="true"

                                className="packages-hubcontent absolute-fill"
                                hubViewState={this.state.hubState}
                                commands={this._getMainCommands()}
                                hideFullScreenToggle={true}
                                viewActions={this._getMainViewActions()}
                                onRenderFilterBar={(): JSX.Element => {
                                    return (
                                        this.getGridMessage() !== MessageState.EmptyFeed &&
                                        this.getGridMessage() !== MessageState.DeletedFeed && (
                                            <PackageFilterBar
                                                pageContext={this.context.pageContext}
                                                selectedFeed={this.state.selectedFeed}
                                                feedViews={this.state.feedViews}
                                                hubState={this.state.hubState}
                                            />
                                        )
                                    );
                                }}
                            >
                                {this.isNewHeaderEnabled ? (
                                    // <Hub> parent throws if first element not a <HubHeader>
                                    <HubHeader />
                                ) : (
                                        <HubHeader
                                            headerItemPicker={FeedPickerHelper.getFeedPicker(
                                                this.getFeedPickList(),
                                                this.state.selectedFeed
                                            )}
                                            hubBreadcrumbAriaLabel={PackageResources.FeedPicker_AriaLabel}
                                        />
                                    )}
                                {this.isNewHeaderEnabled ? <FeedPicker /> : null}
                                <PivotBarItem
                                    className="packages-pivotBarItem"
                                    itemKey={HubViewDefaultPivots.feed}
                                    name="" // Note: This is the only pivot and it is not shown so leaving blank
                                    commands={this._getPackagesPivotCommands()}
                                >
                                    <LoadingContainer isLoading={this.state.packagesLoading}>
                                        {this.getGridMessage() === MessageState.DeletedFeed && <DeletedFeedPanel />}
                                        {this.getGridMessage() === MessageState.EmptyFeed && (
                                            <EmptyFeedPanel
                                                pageContext={this.context.pageContext}
                                                feed={this.state.selectedFeed}
                                                feedViews={this.state.feedViews}
                                                protocolMap={this.state.protocolMap}
                                            />
                                        )}
                                        {this.getGridMessage() !== MessageState.DeletedFeed &&
                                            this.getGridMessage() !== MessageState.EmptyFeed && (
                                                <div
                                                    className="package-list"
                                                    aria-label={PackageResources.AriaLabel_PackageList}
                                                >
                                                    <PackageGrid {...this.feedStateToPackageGridProps()} />
                                                </div>
                                            )}
                                    </LoadingContainer>
                                </PivotBarItem>
                            </Hub>
                            {this.state.selectedPackages &&
                                this.state.selectedPackages.length > 0 &&
                                this.state.isMultiPromotePanelOpen && (
                                    <MultiPromotePanel
                                        isOpen={this.state.isMultiPromotePanelOpen}
                                        selectedPackages={this.state.selectedPackages}
                                        views={this.state.feedViews}
                                        feed={this.state.selectedFeed}
                                        protocolMap={this.state.protocolMap}
                                        mruViewId={this.state.mruViewId}
                                        multiCommandDropdownVersions={this.state.multiCommandDropdownVersions}
                                    />
                                )}
                            {this.state.showCreateBadgePanel && (
                                <CreateBadgePanel
                                    pageContext={this.context.pageContext}
                                    feed={this.state.selectedFeed}
                                    views={this.state.feedViews}
                                    selectedPackage={this.state.selectedPackages[0]}
                                />
                            )}
                            <WrappedCreateFeedPanel />
                            <WrappedIvyPromotePanel />
                        </div>
                    )}
            </div>
        );
    }

    private getFeedPickList(): Feed[] {
        // The feed pick list contains all collection scoped feeds and all feeds in the same project
        if (!this.state.feeds || this.state.feeds.length === 0) {
            return this.state.feeds;
        }

        const currentProjectId = getCurrentSelectedProjectId(this.context.pageContext);
        return this.state.feeds.filter(
            feed => !feed.project || Utils_String.equals(feed.project.id, currentProjectId, /*ignoreCase*/ true)
        );
    }

    private feedStateToPackageGridProps(): IPackageGridProps {
        const packageGridProps: IPackageGridProps = {
            pageContext: this.context.pageContext,
            feed: this.state.selectedFeed,
            packages: this.state.packages?.filter(pkg => pkg.versions?.length > 0),
            requestedPackageCount: this.state.requestedPackageCount,
            nextPageLoading: this.state.nextPageLoading,
            pageSize: this.state.pageSize,
            filterLoading: this.state.packagesLoading,
            protocolMap: this.state.protocolMap,
            upstreamSourceEnabled: this.state.selectedFeed != null && this.state.selectedFeed.upstreamEnabled,
            messageState: this.getGridMessage(),
            isMultiPromotePanelOpen: this.state.isMultiPromotePanelOpen,
            feedViews: this.state.feedViews,
            selectedPackages: this.state.selectedPackages,
            metricsEnabled: this.state.metricsEnabled,
            packageIdMetricsMap: this.state.packageIdMetricsMap
        };
        return packageGridProps;
    }

    private getGridMessage(): MessageState {
        const filterText = PackageFilterBarHelper.getKeywordFilterTextFromFilter(this.state.hubState.filter);
        return PackageGridMessages.getMessageForFeed(this.state.selectedFeed, this.state.packages, filterText);
    }

    private _connectToFeed(): void {
        if (isFeatureFlagEnabled(this.context.pageContext, "Packaging.Feed.ConnectToFeed.EnableUI")) {
            this.props.showConnectToFeed();
        } else {
            OpenConnectToFeedDialog(
                this.context.pageContext,
                this.state.selectedFeed,
                this.state.feedViews,
                this.state.protocolMap,
                HubAction.Feed
            );
        }
    }

    private _getPackagesPivotCommands(): IPivotBarAction[] {
        let commands: IPackageCommand[] = [];
        const packageCommandsGetter: CommandGetters.PackageCommandsGetter = new CommandGetters.PackageCommandsGetter();
        const pivotActionsGetter: PivotGetters.PivotBarActionsGetter = new PivotGetters.PivotBarActionsGetter();

        if (!this.state.selectedPackages || this.state.selectedPackages.length === 0) {
            return [];
        }

        if (this.state.selectedPackages.length === 1) {
            const packageO: Package = this.state.selectedPackages[0];
            commands = packageCommandsGetter.getSingleSelectionItems(
                this.state.selectedFeed,
                this.state.protocolMap[packageO.protocolType],
                packageO,
                packageO.versions[0] as PackageVersion,
                true,
                this.context.pageContext
            );
        } else {
            commands = packageCommandsGetter.getMultipleSelectionItems(
                this.state.selectedFeed,
                this.state.protocolMap,
                this.state.selectedPackages,
                this.context.pageContext
            );
        }

        return pivotActionsGetter.GetPivotBarActions(commands);
    }

    private _getMainViewActions(): IPivotBarAction[] {
        const settingsActions = [];
        const feedSettings: IPivotBarAction = {
            key: "settings",
            important: true,
            iconProps: {
                iconType: VssIconType.fabric,
                iconName: "Settings"
            } as IVssIconProps,
            title: PackageResources.Feed_Settings_Feed_ButtonTitle,
            ariaLabel: PackageResources.Feed_Settings_Feed_ButtonTitle,
            disabled: this.state.feedUser.isAnonymousOrPublic,
            onClick: this.navigateToSettings
        };
        settingsActions.push(feedSettings);

        if (this.state.feedUser.canAdministerFeeds === true) {
            const brandingName = Utils_String.format(
                PackageResources.Feed_Settings_Extension_ButtonTitle,
                PackageResources.AzureArtifacts
            );
            const globalSettings: IPivotBarAction = {
                important: true,
                iconProps: {
                    iconType: VssIconType.fabric,
                    iconName: "PeopleSettings"
                } as IVssIconProps,
                title: brandingName,
                ariaLabel: brandingName,
                disabled: this.state.feedUser.isAnonymousOrPublic,
                key: "globalsettings",
                onClick: this.openGlobalSettingsDialog
            };
            settingsActions.push(globalSettings);
        }

        return settingsActions;
    }

    private _getMainCommands(): IPivotBarAction[] {
        const commands: IPivotBarAction[] = [];
        const isPublic: boolean = isPublicFeed(this.state.selectedFeed);

        if (isPublic) {
            const publicBadge: IPivotBarAction = {
                key: "publicBadge",
                important: true,
                name: PackageResources.Public,
                iconProps: {
                    iconType: VssIconType.fabric,
                    iconName: "Globe"
                }
            };
            commands.push(publicBadge);
        }

        const createNewFeedCommand: IPivotBarAction = {
            key: "createnewfeed",
            important: true,
            name: PackageResources.NewFeedCommand,
            onClick: this.props.showCreateFeed,
            iconProps: {
                iconType: VssIconType.bowtie,
                iconName: "bowtie-icon bowtie-math-plus-light"
            },
            disabled: !this.state.feedUser.canCreateFeed
        };
        commands.push(createNewFeedCommand);

        const connectToFeedCommand: IPivotBarAction = {
            key: "connect",
            name: PackageResources.ConnectToFeedButtonText,
            important: true,
            iconProps: {
                iconName: "bowtie-icon bowtie-plug-outline connect-to-feed-button-icon",
                iconType: VssIconType.bowtie
            },
            onClick: this._connectToFeed.bind(this),
            disabled: this.state.selectedFeed.deletedDate != null
        };
        commands.push(connectToFeedCommand);

        const recycleBinAction: IPivotBarAction = {
            key: "recycleBin",
            name: PackageResources.RecycleBinButtonText,
            important: true,
            iconProps: {
                iconType: VssIconType.bowtie,
                iconName: "bowtie-recycle-bin"
            } as IVssIconProps,
            onClick: () => RecycleBinActions.RecycleBinClicked.invoke({}),
            disabled:
                !hasAccessToBaseFeed(this.state.selectedFeed) ||
                this.state.feedUser.isAnonymousOrPublic ||
                this.state.selectedFeed.deletedDate != null
        } as IPivotBarAction;
        commands.push(recycleBinAction);

        return commands;
    }

    private navigateToSettings = (): void => {
        SettingsActions.FeedSettingsNavigateClicked.invoke({});
    };

    private openGlobalSettingsDialog = (): void => {
        if (isFeatureFlagEnabled(this.context.pageContext, "Packaging.Feed.GlobalPermissionsMapWellKnownIdentifier")) {
            // Display the Redux-based global settings panel
            this.props.showGlobalSettings();
        } else {
            // Show the 'legacy' global settings dialog
            this.actionCreator.showPackageManagementSettingDialog.invoke(null);
        }
    };

    public getStore(): FeedStore {
        return this.props.fluxStore;
    }

    public getState(): FeedState {
        return this.getStore().getFeedState();
    }

    private isNewHeaderEnabled: boolean;
    private triggerRender = () => this.setState(this.getState());
    private actionCreator: FeedActionCreator;
}

function mapStateToProps(state: IFeedHubViewAwareState): {} {
    return {};
}

const ConnectedFeedControllerView = connect(mapStateToProps, Actions)(FeedControllerView);

function LegacyFeedControllerView(props: ILegacyControllerViewProps) {
    return <ConnectedFeedControllerView fluxStore={props.feedFluxStore} />;
}

VssComponent.register("packagelist", LegacyFeedControllerView);
